import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import api from "../../services/api";
import Navbar from "../../components/navbar/navbar";
import "../tablestorage/tablestorage.scss";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { RotateCcw, CirclePlus, Pencil, Trash2, X, Search } from "lucide-react";
import Swal from "sweetalert2";

const DATA_INICIAL = [];

const emptyForm = {
  nombre: "",
  unidad_medida: "",
  cantidad: "",
  cantidad_min: "",
  cantidad_max: "",
  ubicacion: "",
  codigo: "",
  proveedor: "",
};



const isNumeric = (val) => /^\d+(\.\d+)?$/.test(String(val).trim());

const isUnitOk = (val) =>
  /^[a-zA-ZáéíóúÁÉÍÓÚñÑ0-9\s\/\-\.\(\)³²]+$/.test(String(val).trim());

const Tablestorage = () => {
  const navigate = useNavigate();
  const { almacenId } = useParams();
  const location = useLocation();
  const almacen = location.state?.almacen;

  const [q, setQ] = useState("");
  const [filtros, setFiltros] = useState({
    nombre: "Todos",
    cantidad: "Todos",
    cantidad_min: "Todos",
    cantidad_max: "Todos",
    ubicacion: "Todos",
    codigo: "Todos",
    proveedor: "Todos",
  });

  const [rows, setRows] = useState(DATA_INICIAL);

  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const isEditing = editingId !== null;

  const setFiltro = (key, value) =>
    setFiltros((prev) => ({ ...prev, [key]: value }));

  const fetchInventario = async () => {
  try {
    const res = await api.get(`/almacenes/${almacenId}/inventario`);
    setRows(res.data.items || []);
  } catch (e) {
    console.error("Error al cargar inventario:", e);
    setRows([]);
  }
};

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nombre: row.nombre || "",
      unidad_medida: row.unidad_medida || "",
      cantidad: row.cantidad ?? "",
      cantidad_min: row.cantidad_min ?? "",
      cantidad_max: row.cantidad_max ?? "",
      ubicacion: row.ubicacion || "",
      codigo: row.codigo || "",
      proveedor: row.proveedor || "",
    });
    setErrors({});
    setOpenModal(true);
  };

  const closeAdd = () => {
    setOpenModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAdd();
    };
    if (openModal) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openModal]);

  useEffect(() => {
    fetchInventario();
  }, [almacenId]);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const options = useMemo(() => {
    const uniq = (arr) =>
      Array.from(
        new Set(
          arr.filter(
            (v) => v !== null && v !== undefined && String(v).trim() !== ""
          )
        )
      );

    const sortNumericStrings = (arr) => [...arr].sort((a, b) => Number(a) - Number(b));

    const nombres = uniq(rows.map((r) => r.nombre)).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    const ubicaciones = uniq(rows.map((r) => r.ubicacion)).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    const codigos = uniq(rows.map((r) => r.codigo)).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    const proveedores = uniq(rows.map((r) => r.proveedor)).sort((a, b) =>
      String(a).localeCompare(String(b))
    );

    const cantidades = sortNumericStrings(uniq(rows.map((r) => String(r.cantidad))));
    const cantidadesMin = sortNumericStrings(uniq(rows.map((r) => String(r.cantidad_min))));
    const cantidadesMax = sortNumericStrings(uniq(rows.map((r) => String(r.cantidad_max))));

    return {
      nombre: nombres,
      ubicacion: ubicaciones,
      codigo: codigos,
      proveedor: proveedores,
      cantidad: cantidades,
      cantidad_min: cantidadesMin,
      cantidad_max: cantidadesMax,
    };
  }, [rows]);

  const validate = () => {
    const e = {};

    if (!form.nombre.trim()) e.nombre = "Requerido";
    if (!form.codigo.trim()) e.codigo = "Requerido";
    if (!form.ubicacion.trim()) e.ubicacion = "Requerido";
    if (!form.proveedor.trim()) e.proveedor = "Requerido";

    if (!String(form.cantidad).trim()) e.cantidad = "Requerido";
    else if (!isNumeric(form.cantidad)) e.cantidad = "Solo números";

    if (!String(form.cantidad_min).trim()) e.cantidad_min = "Requerido";
    else if (!isNumeric(form.cantidad_min)) e.cantidad_min = "Solo números";

    if (!String(form.cantidad_max).trim()) e.cantidad_max = "Requerido";
    else if (!isNumeric(form.cantidad_max)) e.cantidad_max = "Solo números";

    if (!form.unidad_medida.trim()) e.unidad_medida = "Requerido";
    else if (!isUnitOk(form.unidad_medida)) e.unidad_medida = "Unidad inválida";

    if (!e.cantidad && !e.cantidad_min && !e.cantidad_max) {
      const c = Number(form.cantidad);
      const min = Number(form.cantidad_min);
      const max = Number(form.cantidad_max);

      if (min > max) e.cantidad_min = "Min no puede ser > Max";
      if (c < min) e.cantidad = "Cantidad no puede ser < Min";
      if (c > max) e.cantidad = "Cantidad no puede ser > Max";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const blockNonNumericKeys = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
  };

  const normalizeRow = (item, payload) => ({
    id: item?.id ?? Date.now(),
    nombre: item?.nombre ?? payload.nombre,
    unidad_medida: item?.unidad_medida ?? payload.unidad_medida,
    cantidad: item?.cantidad ?? payload.cantidad_actual,
    cantidad_min: item?.cantidad_min ?? payload.cantidad_minima,
    cantidad_max: item?.cantidad_max ?? payload.cantidad_maxima,
    ubicacion: item?.ubicacion ?? payload.ubicacion_nombre,
    codigo: item?.codigo ?? payload.codigo,
    proveedor: item?.proveedor ?? payload.proveedor_nombre,
  });

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      const payload = {
        nombre: form.nombre.trim(),
        codigo: form.codigo.trim(),
        unidad_medida: form.unidad_medida.trim(),
        cantidad_actual: Number(form.cantidad),
        cantidad_minima: Number(form.cantidad_min),
        cantidad_maxima: Number(form.cantidad_max),
        ubicacion_nombre: form.ubicacion.trim(),
        proveedor_nombre: form.proveedor.trim(),
      };

      let res;

      if (isEditing) {
        res = await api.put(
          `/almacenes/${almacenId}/insumos/${editingId}`,
          payload
        );
      } else {
        res = await api.post(
          `/almacenes/${almacenId}/insumos`,
          payload
        );
      }

      const item = res.data?.item || res.data;
      const row = normalizeRow(item, payload);

      if (isEditing) {
        setRows((prev) => prev.map((r) => (r.id === editingId ? row : r)));
      } else {
        setRows((prev) => [row, ...prev]);
      }

      closeAdd();

      await Swal.fire({
        title: isEditing ? "Actualizado" : "Creado",
        text: isEditing
          ? "El insumo fue actualizado correctamente."
          : "El insumo fue creado correctamente.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;

      if (backendErrors) {
        const mapped = {};
        if (backendErrors.nombre) mapped.nombre = backendErrors.nombre[0];
        if (backendErrors.codigo) mapped.codigo = backendErrors.codigo[0];
        if (backendErrors.unidad_medida) mapped.unidad_medida = backendErrors.unidad_medida[0];

        if (backendErrors.cantidad_actual) mapped.cantidad = backendErrors.cantidad_actual[0];
        if (backendErrors.cantidad_minima) mapped.cantidad_min = backendErrors.cantidad_minima[0];
        if (backendErrors.cantidad_maxima) mapped.cantidad_max = backendErrors.cantidad_maxima[0];

        if (backendErrors.ubicacion_nombre) mapped.ubicacion = backendErrors.ubicacion_nombre[0];
        if (backendErrors.proveedor_nombre) mapped.proveedor = backendErrors.proveedor_nombre[0];

        setErrors((prev) => ({ ...prev, ...mapped }));
        return;
      }

      await Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Error al guardar insumo.",
        icon: "error",
        confirmButtonColor: "#4f46e5",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar insumo?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/almacenes/${almacenId}/insumos/${id}`);

      setRows((prev) => prev.filter((r) => r.id !== id));

      await Swal.fire({
        title: "Eliminado",
        text: "El insumo fue eliminado correctamente.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "No se pudo eliminar el insumo.",
        icon: "error",
        confirmButtonColor: "#4f46e5",
      });
    }
  };

  const resetFiltros = () => {
    setQ("");
    setFiltros({
      nombre: "Todos",
      cantidad: "Todos",
      cantidad_min: "Todos",
      cantidad_max: "Todos",
      ubicacion: "Todos",
      codigo: "Todos",
      proveedor: "Todos",
    });
  };

  const data = useMemo(() => {
    return rows.filter((r) => {
      const matchFiltro = (campo, key) =>
        filtros[key] === "Todos" ? true : String(r[campo]) === String(filtros[key]);

      const matchBusqueda =
        q.trim().length === 0
          ? true
          : Object.values(r).some((v) =>
              String(v).toLowerCase().includes(q.toLowerCase())
            );

      return (
        matchBusqueda &&
        matchFiltro("nombre", "nombre") &&
        matchFiltro("cantidad", "cantidad") &&
        matchFiltro("cantidad_min", "cantidad_min") &&
        matchFiltro("cantidad_max", "cantidad_max") &&
        matchFiltro("ubicacion", "ubicacion") &&
        matchFiltro("codigo", "codigo") &&
        matchFiltro("proveedor", "proveedor")
      );
    });
  }, [q, filtros, rows]);

  return (
    <div className="body-table">
      <Navbar />

      <div className="general">
        <div className="header-table">
          <div className="table-info">
            <h1 className="table-title">
              {almacen?.nombre || `Almacén #${almacenId}`}
            </h1>
            <p className="table-subtitle">Catálogo de productos.</p>
          </div>

          <div className="buttons-tl">
            <button className="Add" type="button" onClick={openAdd}>
              <CirclePlus size={18} />
              <span>Agregar</span>
            </button>

            <button className="filter" type="button" onClick={resetFiltros}>
              <RotateCcw size={12} />
              <span>Reiniciar filtros</span>
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "10px 0 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #e6eaf2",
              background: "#fff",
              width: 360,
              maxWidth: "100%",
            }}
          >
            <Search size={16} style={{ opacity: 0.7 }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar en la tabla..."
              style={{ border: 0, outline: "none", width: "100%", background: "transparent" }}
            />
          </div>

          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Mostrando: <b>{data.length}</b> de <b>{rows.length}</b>
          </div>
        </div>

        <div className="table-colab">
          <div className="tabla-wrap">
            <table className="tabla">
              <thead>
                <tr className="tabla__head">
                  <th>MATERIA PRIMA</th>
                  <th>CANTIDAD EN PLANTA</th>
                  <th>CANTIDAD MIN</th>
                  <th>CANTIDAD MAX</th>
                  <th>UBICACIÓN EN PLANTA</th>
                  <th>CÓDIGO DEL PRODUCTO</th>
                  <th>NOMBRE DEL PROVEEDOR</th>
                  <th className="tabla__acciones sticky-right">ACCIONES</th>
                </tr>

                <tr className="tabla__filters">
                  <th>
                    <select value={filtros.nombre} onChange={(e) => setFiltro("nombre", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.nombre.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.cantidad} onChange={(e) => setFiltro("cantidad", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.cantidad.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.cantidad_min} onChange={(e) => setFiltro("cantidad_min", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.cantidad_min.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.cantidad_max} onChange={(e) => setFiltro("cantidad_max", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.cantidad_max.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.ubicacion} onChange={(e) => setFiltro("ubicacion", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.ubicacion.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.codigo} onChange={(e) => setFiltro("codigo", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.codigo.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select value={filtros.proveedor} onChange={(e) => setFiltro("proveedor", e.target.value)}>
                      <option value="Todos">Todos</option>
                      {options.proveedor.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </th>

                  <th className="sticky-right" />
                </tr>
              </thead>

              <tbody>
                {data.map((r) => (
                  <tr key={r.id}>
                    <td>{r.nombre}</td>
                    <td>{r.cantidad} {r.unidad_medida}</td>
                    <td>{r.cantidad_min} {r.unidad_medida}</td>
                    <td>{r.cantidad_max} {r.unidad_medida}</td>
                    <td>{r.ubicacion}</td>
                    <td>{r.codigo}</td>
                    <td>{r.proveedor}</td>

                    <td className="tabla__acciones center sticky-right">
                      <div className="acciones-row">
                        <button
                          className="icon-btn"
                          title="Editar"
                          onClick={() => handleEdit(r)}
                        >
                          <Pencil size={18} />
                        </button>

                        <button
                          className="icon-btn"
                          title="Eliminar"
                          onClick={() => handleDelete(r.id)}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {data.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={8}>
                      Sin resultados
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {openModal && (
        <div className="modal-overlay" onMouseDown={closeAdd}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">
                  {isEditing ? "Editar insumo" : "Registrar nuevo insumo"}
                </h2>
                <p className="modal-subtitle">
                  {isEditing
                    ? "Modifica la información del producto."
                    : "Captura la información del producto."}
                </p>
              </div>

              <button className="modal-close" onClick={closeAdd}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className={`field ${errors.nombre ? "has-error" : ""}`}>
                  <label>Materia prima</label>
                  <input
                    value={form.nombre}
                    onChange={(e) => handleChange("nombre", e.target.value)}
                    placeholder="Ej. Gasket"
                  />
                  {errors.nombre && <span className="error">{errors.nombre}</span>}
                </div>

                <div className={`field ${errors.cantidad ? "has-error" : ""}`}>
                  <label>Cantidad en planta</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    value={form.cantidad}
                    onKeyDown={blockNonNumericKeys}
                    onChange={(e) => handleChange("cantidad", e.target.value)}
                    placeholder="Ej. 20000"
                  />
                  {errors.cantidad && <span className="error">{errors.cantidad}</span>}
                </div>

                <div className={`field ${errors.unidad_medida ? "has-error" : ""}`}>
                  <label>Unidad de medida</label>
                  <input
                    value={form.unidad_medida}
                    onChange={(e) => handleChange("unidad_medida", e.target.value)}
                    placeholder="Ej. pz, kg, L"
                  />
                  {errors.unidad_medida && <span className="error">{errors.unidad_medida}</span>}
                </div>

                <div className={`field ${errors.cantidad_min ? "has-error" : ""}`}>
                  <label>Cantidad min</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    value={form.cantidad_min}
                    onKeyDown={blockNonNumericKeys}
                    onChange={(e) => handleChange("cantidad_min", e.target.value)}
                    placeholder="Ej. 10000"
                  />
                  {errors.cantidad_min && <span className="error">{errors.cantidad_min}</span>}
                </div>

                <div className={`field ${errors.cantidad_max ? "has-error" : ""}`}>
                  <label>Cantidad max</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    step="1"
                    value={form.cantidad_max}
                    onKeyDown={blockNonNumericKeys}
                    onChange={(e) => handleChange("cantidad_max", e.target.value)}
                    placeholder="Ej. 50000"
                  />
                  {errors.cantidad_max && <span className="error">{errors.cantidad_max}</span>}
                </div>

                <div className={`field ${errors.ubicacion ? "has-error" : ""}`}>
                  <label>Ubicación en planta</label>
                  <input
                    value={form.ubicacion}
                    onChange={(e) => handleChange("ubicacion", e.target.value)}
                    placeholder="Ej. Planta A - Rack 3"
                  />
                  {errors.ubicacion && <span className="error">{errors.ubicacion}</span>}
                </div>

                <div className={`field ${errors.codigo ? "has-error" : ""}`}>
                  <label>Código del producto</label>
                  <input
                    value={form.codigo}
                    onChange={(e) => handleChange("codigo", e.target.value)}
                    placeholder="Ej. GASK-CUB-001"
                  />
                  {errors.codigo && <span className="error">{errors.codigo}</span>}
                </div>

                <div className={`field ${errors.proveedor ? "has-error" : ""}`}>
                  <label>Nombre del proveedor</label>
                  <input
                    value={form.proveedor}
                    onChange={(e) => handleChange("proveedor", e.target.value)}
                    placeholder="Ej. Sergio Tapia"
                  />
                  {errors.proveedor && <span className="error">{errors.proveedor}</span>}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" type="button" onClick={closeAdd} disabled={saving}>
                Cancelar
              </button>

              <button className="btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving
                  ? (isEditing ? "Actualizando..." : "Guardando...")
                  : (isEditing ? "Actualizar insumo" : "Guardar insumo")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tablestorage;